/*
 *  (c) 2015 - 2024 ENISCO by FORCAM GmbH
 */

import React, {useMemo} from 'react'
import {Box} from '@mui/material'
import {Theme, useTheme} from '@mui/material/styles'
import useProfile from '../../hooks/useProfile'
import {App} from '../../model/apps'
import appIdConverter from '../helper/appIdConverter'
import useRegisteredApps from '../../hooks/useRegisteredApps'
import useFavoriteApps from '../../hooks/useFavoriteApps'
import QuickLaunchApp from './QuickLaunchApp'
import update from 'immutability-helper'
import QuickLaunchDelete from './QuickLaunchDelete'
import {MccDraggable, MccDraggablePreview} from '@enisco/mcc-react-lib'
import useMediaQuery from '@mui/material/useMediaQuery'

const QuickLaunch: React.FC = () => {
  const [profile, setProfile] = useProfile()
  const [registeredApps] = useRegisteredApps()
  const [registeredFavorites] = useFavoriteApps()
  const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const theme = useTheme()

  const backgroundColor = useMemo(
    () => ({
      // @ts-ignore issue with module augmentation, we ensured the value is there though
      backgroundColor: theme.palette.background.dark
    }),
    [theme]
  )

  const apps: App[] = appIdConverter(
    profile?.quickLaunchApps ?? [],
    registeredApps,
    registeredFavorites
  )

  const quickLaunchApps = () => {
    if (profile?.quickLaunchCustomized == undefined) {
      const sortedApps = registeredApps.length > 0 ? registeredApps.filter((a) => a.order != undefined).
      toSorted((a, b) => {
        return a.order - b.order
      }) : []

      setProfile({quickLaunchApps: sortedApps, quickLaunchCustomized: false})
      return sortedApps
    }

    if (!profile?.quickLaunchCustomized) {
      const currentQuickLaunchApps = profile.quickLaunchApps
      const newQuickLaunchApps = registeredApps.length > 0 ? registeredApps.filter((a) => a.order != undefined) : []

      if (currentQuickLaunchApps.length != newQuickLaunchApps.length) {
        setProfile({quickLaunchApps: newQuickLaunchApps, quickLaunchCustomized: false})
      }
    }

    return apps
  }

  const moveApp = (dragIndex: number, hoverIndex: number) => {
    const newApps = update(apps, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, apps[dragIndex]]
      ]
    })
    setProfile({quickLaunchApps: newApps, quickLaunchCustomized: true})
  }

  const dragDropApp = (app: App, index: number) => {
    return (
      <MccDraggable
        index={index}
        key={`mcc-draggable-${app.app}-${index}`}
        identifier={'QuickLaunchApp'}
        object={app}
        moveItem={moveApp}
      >
        <QuickLaunchApp
          key={`QuickLaunchApp-${app.app}-${index}`}
          app={app}
          registeredFavorites={registeredFavorites}
          quickLaunchBarType={profile?.quickLaunchType ?? 'left'}
        />
      </MccDraggable>
    )
  }

  if (profile === undefined || profile.quickLaunchType === 'none' || hidden) {
    return
  }

  return (
    <Box
      bgcolor={backgroundColor}
      sx={{
        height: 'calc(100vh - 64px)',
        width: '63px',
        float: 'left'
      }}
      data-testid={'QuickLaunch:drag_drop_context'}
    >
      {quickLaunchApps().map(dragDropApp)}
      <MccDraggablePreview identifier={'QuickLaunchApp'} />
      <Box
        sx={{
          width: '32px',
          height: '32px',
          bottom: 22,
          position: 'absolute',
          padding: '12px'
        }}
      >
        <QuickLaunchDelete />
      </Box>
    </Box>
  )
}

export default QuickLaunch
